// The ONE sign-out, for every control that offers it (the topbar, the waitlist
// wall). It used to be copied per screen, and the copies drifted: the topbar's
// retired this browser's push subscription (05-7, plan 101) and the waitlist
// wall's did not. `sign-out.test.ts` refuses a second caller of `/auth/logout`.
//
// Order is the contract:
//   1. Retire push (`retirePushOnSignOut`) while the session can still delete
//      its server row. A subscription belongs to the USER, so left alone the next
//      person at this browser keeps getting their notices. Bounded (at most
//      2 × `PUSH_RETIRE_MS`) and never rejects, so it cannot block step 2.
//   2. Revoke the session.
//   3. Leave for `/` ONLY after a successful revoke: on a failed logout the
//      cookie may still be live, so bouncing to `/` would silently
//      re-authenticate the user (a no-op sign-out). Say so and stay put.
//      A successful revoke first forgets what this browser holds for the user
//      and no one else: the Terminal's command history
//      (`terminal/command-history-store.ts`). A failed one keeps it — the same
//      user is still signed in.

import { t } from '../i18n';
import { API_BASE_URL } from './api';
import { browserPushRuntime, type PushRuntime, retirePushOnSignOut } from './push';
import { forgetCommandHistory } from './terminal/command-history-store';

/** The side effects, injectable so the order is testable without a browser. */
export type SignOutDeps = {
  readonly push: PushRuntime;
  /** POST the revoke; `true` when the API accepted it. */
  readonly logout: () => Promise<boolean>;
  /** Hard-reload onto `/` so <App/> re-probes → <Login/>. */
  readonly leave: () => void;
  /** Surface a failed revoke. ponytail: `alert` — a toast is overkill here. */
  readonly fail: () => void;
};

async function postLogout(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false; // network/CORS failure — the session may still be live
  }
}

function browserDeps(): SignOutDeps {
  return {
    push: browserPushRuntime(),
    logout: postLogout,
    leave: () => window.location.assign('/'),
    fail: () => window.alert(t('errors.generic')),
  };
}

export async function signOut(deps: SignOutDeps = browserDeps()): Promise<void> {
  await retirePushOnSignOut(deps.push);
  if (!(await deps.logout())) return deps.fail();
  forgetCommandHistory();
  deps.leave();
}

/**
 * Sign out, then reload THIS page instead of leaving for `/` — the "sign in
 * again" a step-up refusal asks for (`<OperatorRefusalAlert/>`, #5724). Signed
 * out, `<App/>` renders `<Login/>` at this very path (#475), which offers every
 * leg (GitHub, org SSO, work email) and hands each `return_to` = this page
 * (`currentReturnTo`), so the operator lands back where they were.
 *
 * Not `/login?return_to=…`: the login screen relays only the API's
 * `/oauth/authorize` target (`handedAuthorizeTarget`, `./return-to.ts`) and
 * would drop a dashboard page by design.
 */
export function signInAgain(deps: SignOutDeps = browserDeps()): Promise<void> {
  const here = `${window.location.pathname}${window.location.search}`;
  return signOut({ ...deps, leave: () => window.location.assign(here) });
}
